import { SalonStockerHttpTypes } from "@repo/api-sdk";
import { getProductSupplierOffers } from "./product";
import { formatPrice } from "./money";
import { resolveLineItemDisplay } from "./cart";
import { getSelectedShippingPickerId } from "./shippingOption";

type StoreSellerCart = SalonStockerHttpTypes.StoreSellerCart;
type StoreSeller = SalonStockerHttpTypes.StoreSeller;
type StoreSellerOffer = SalonStockerHttpTypes.StoreSellerOffer;
type StoreProductWithPricing =
  SalonStockerHttpTypes.StoreProductWithPricing<"meta">;

/** Ignore sub-cent rounding noise when deciding whether a saving is real. */
const EPSILON = 0.005;

/**
 * The active-scoped price extremes across one product's offers: which seller
 * has the cheapest ACTIVE offer (the "Cheapest" tag) and the highest active
 * price (the ceiling the current pick's saving is measured against).
 *
 * Active-scoped on purpose - it is the single owner of what "cheapest" means
 * in the comparison matrix. A "Cheapest" tag the buyer can't act on is a lie,
 * and a saving measured against an offer they can't buy isn't real, so a
 * cheaper (or dearer) OUT_OF_STOCK/INACTIVE offer never wins here - unlike a
 * raw price scan. Ties are broken by the incoming order (offers arrive
 * active-first, then by ascending price, from `getProductSupplierOffers`).
 *
 * `cheapestSellerId` is `null` unless at least TWO active offers compete: a
 * single buyable supplier has nothing to be cheapest against (the active-scoped
 * generalization of the old "only one supplier" rule). `highestActivePrice` is
 * `null` when no offer is active.
 */
export function activePriceExtremes(
  offers: SalonStockerHttpTypes.StoreSellerOffer[]
): { cheapestSellerId: string | null; highestActivePrice: number | null } {
  let cheapestSellerId: string | null = null;
  let cheapestPrice = Infinity;
  let highestActivePrice: number | null = null;
  let activeCount = 0;

  for (const offer of offers) {
    if (offer.status !== SalonStockerHttpTypes.StoreSellerOfferStatus.ACTIVE) {
      continue;
    }
    activeCount += 1;
    const price = offer.calculated_price.calculated_amount;
    if (price < cheapestPrice) {
      cheapestPrice = price;
      cheapestSellerId = offer.seller_id;
    }
    if (highestActivePrice === null || price > highestActivePrice) {
      highestActivePrice = price;
    }
  }

  return {
    cheapestSellerId: activeCount >= 2 ? cheapestSellerId : null,
    highestActivePrice
  };
}

/**
 * Every distinct product across the customer's seller-carts, regardless of
 * which suppliers are currently checked in the left filter sidebar - this
 * comparison view is deliberately unfiltered by that selection.
 *
 * Sorted (not just deduped) so the result is stable regardless of which order
 * `carts`/their items happen to iterate in. That order isn't guaranteed
 * across renders - switching a product's supplier changes the underlying
 * carts array shape (a cart can appear, disappear, or shift position), and
 * this array is later embedded verbatim in a query cache key
 * (`useProductComparisonPricing`). An unsorted array would turn a same-content
 * reorder into a cache miss - a real loading state that flashes the whole
 * matrix to its skeleton and back on every switch, which is the "table jumps
 * around" bug this guards against.
 */
export function collectDistinctProductIds(carts: StoreSellerCart[]): string[] {
  const ids = new Set<string>();
  for (const cart of carts) {
    for (const item of cart.items) {
      if (item.product_id) {
        ids.add(item.product_id);
      }
    }
  }
  return [...ids].sort();
}

export interface CurrentSupplierPick {
  sellerId: string;
  quantity: number;
  productTitle: string;
  /** The chosen option, or `null` for a product with no real variants. */
  variantTitle: string | null;
  /** The seller-cart currently holding this product - the "switch supplier" source. */
  cartId: string;
  /** The line item to remove from `cartId` when switching this product away. */
  lineItemId: string;
  /**
   * The line item's own unit price - the fallback shown when the pricing
   * endpoint didn't return this product at all (see `buildComparisonMatrix`),
   * so the row still has a real number instead of nothing.
   */
  unitPrice: number;
}

/**
 * "Which supplier does the customer currently have this product from" is not
 * a field on the line item itself - a seller-cart line item has no seller/offer
 * id (see `StoreSellerCartLineItem`). The seller only lives at the parent
 * `StoreSellerCart.seller.id`, so the picks are derived by scanning every
 * cart's line items.
 *
 * One pick per LINE, not per product: the same product (or two variants of
 * it) can sit in two suppliers' carts at once, and the buyer pays for every
 * one of those lines. Collapsing them to a single pick per product made the
 * matrix show one line while the Total included both (KAN-260).
 */
export function deriveCurrentPicksByProduct(
  carts: StoreSellerCart[]
): Record<string, CurrentSupplierPick[]> {
  const picks: Record<string, CurrentSupplierPick[]> = {};
  for (const cart of carts) {
    for (const item of cart.items) {
      if (!item.product_id) continue;
      // Same resolver the cart list and order summary use, so the matrix
      // names a product and its variant exactly as the other surfaces do -
      // including hiding Medusa's placeholder variant. No `sellerName` arg:
      // it only suppresses the subtitle line, which the matrix doesn't show.
      const display = resolveLineItemDisplay(item);
      (picks[item.product_id] ??= []).push({
        sellerId: cart.seller.id,
        quantity: item.quantity,
        productTitle: display.productTitle || item.title || "",
        variantTitle: display.variantTitle,
        cartId: cart.cart_id,
        lineItemId: item.id,
        unitPrice: item.unit_price
      });
    }
  }
  return picks;
}

export type ComparisonCell =
  | { kind: "offer"; offer: StoreSellerOffer }
  | { kind: "not-stocked" }
  /**
   * The customer's own line item, shown when the pricing endpoint didn't
   * return this product at all (a failed/omitted batch in
   * `useProductComparisonPricing`, or a product it doesn't know about) - so
   * the row still appears, priced from the cart itself, instead of the
   * product silently vanishing from the comparison. Never clickable: there's
   * no offer id to switch to, and no basis for comparing against other
   * suppliers without their pricing either.
   */
  | { kind: "current-unpriced"; unitPrice: number };

export interface ComparisonColumn {
  sellerId: string;
  sellerName: string;
  /**
   * The customer's active seller-cart with this supplier, or `null` when this
   * supplier is shown purely for comparison. Only non-null columns have a
   * resolvable free-shipping threshold (it requires a real `cart_id`).
   */
  cartId: string | null;
  /**
   * The shipping service selected on that cart, so the column's free-shipping
   * note can be scoped to it - a threshold makes one service free, and the
   * footer must not promise it to a buyer sitting on another. `null` when
   * there is no cart or nothing selected yet.
   */
  selectedShippingOptionId: string | null;
  /**
   * The handling time every one of this supplier's offers shares, hoisted out
   * of the cells so it can be shown once under the supplier's name instead of
   * repeated down the column.
   *
   * `null` when their offers disagree. Handling time lives on the offer, not
   * the seller, so a supplier CAN quote different times per product even
   * though they normally don't - and a column-level label would then be wrong
   * for some rows. Callers fall back to showing it per cell in that case.
   */
  handlingTime: StoreSellerOffer["handling_time"] | null;
}

/**
 * One row per cart LINE the buyer is paying for, so a product held in two
 * suppliers' carts (or as two variants) appears once per line and the matrix
 * always adds up to the same Total as the cart. `rowKey` is what identifies a
 * row: `productId` repeats across those lines.
 */
export interface ComparisonProductRow {
  /** Stable React / optimistic-state key: the line item id, or the product id for a row with no line. */
  rowKey: string;
  productId: string;
  productTitle: string;
  /**
   * The chosen option, shown under the product name so a row names the same
   * thing the cart list does. `null` for a product with no real variants, or
   * for a comparison-only row the buyer isn't currently buying.
   */
  variantTitle: string | null;
  quantity: number;
  /** The seller the customer currently has this product from, if any. */
  currentSellerId: string | null;
  /** The cart to remove from when switching this product to a different supplier. */
  currentCartId: string | null;
  /** The line item to remove from `currentCartId` when switching suppliers. */
  currentLineItemId: string | null;
  /** The current pick's own per-unit price - the line item's `unit_price`, not a re-derived offer price, so it's correct even for a "current-unpriced" row. `null` when there's no current pick at all. */
  currentUnitPrice: number | null;
  cells: Record<string, ComparisonCell>;
  /** Null when there's only one supplier for this product - nothing to tag. */
  cheapestSellerId: string | null;
  /** Savings vs. the highest shown price; omitted when there's nothing to save. */
  savingFormatted?: string;
}

export interface ComparisonMatrix {
  rows: ComparisonProductRow[];
  columns: ComparisonColumn[];
}

/**
 * Builds the products-by-suppliers grid: every supplier who carries any of
 * the given products as a column, every product as a row, and per-cell offers
 * (or "not stocked"). Reuses `getProductSupplierOffers` (already powers the
 * search page's multi-supplier comparison) with no seller pinning, since this
 * view wants every carrying supplier, not just the ones already in the cart.
 *
 * Every product in `carts` gets a row, guaranteed - even one `pricingProducts`
 * doesn't include (see the "current-unpriced" fallback below). Without that
 * guarantee, a product missing from the pricing response - a failed/omitted
 * batch in `useProductComparisonPricing`, or one it just doesn't recognize -
 * would silently vanish from the comparison instead of still being listed.
 */
export function buildComparisonMatrix(input: {
  carts: StoreSellerCart[];
  pricingProducts: StoreProductWithPricing[];
  sellersMap: Record<string, StoreSeller>;
}): ComparisonMatrix {
  const { carts, pricingProducts, sellersMap } = input;
  const currentPicks = deriveCurrentPicksByProduct(carts);

  const cartBySeller = new Map<string, StoreSellerCart>();
  for (const cart of carts) {
    cartBySeller.set(cart.seller.id, cart);
  }

  const columnsMap = new Map<string, Omit<ComparisonColumn, "handlingTime">>();
  // Tracks whether a seller quotes one handling time across every offer of
  // theirs on screen. Once two offers disagree the entry is pinned to `null`
  // and stays there - see `ComparisonColumn.handlingTime`.
  const handlingBySeller = new Map<
    string,
    StoreSellerOffer["handling_time"] | null
  >();

  // Shared by both row-building passes below (real offers, then the
  // unpriced-cart-item fallback) so a column is only ever created in one place.
  function ensureColumn(sellerId: string) {
    if (!columnsMap.has(sellerId)) {
      const cart = cartBySeller.get(sellerId);
      columnsMap.set(sellerId, {
        sellerId,
        sellerName: sellersMap[sellerId]?.name ?? "Unknown seller",
        cartId: cart?.cart_id ?? null,
        // Composite `base::handle` id for a live rate, so the column's
        // free-shipping footer scopes to the selected service (KAN-108).
        selectedShippingOptionId:
          getSelectedShippingPickerId(cart?.shipping_methods?.[0]) ?? null
      });
    }
  }

  const rows: ComparisonProductRow[] = [];
  const pricedProductIds = new Set<string>();

  for (const product of pricingProducts) {
    pricedProductIds.add(product.id);
    const offers = getProductSupplierOffers(product);
    const picks = currentPicks[product.id] ?? [];

    const cells: Record<string, ComparisonCell> = {};

    for (const offer of offers) {
      const isNewColumn = !columnsMap.has(offer.seller_id);
      ensureColumn(offer.seller_id);
      if (isNewColumn) {
        handlingBySeller.set(offer.seller_id, offer.handling_time);
      } else if (
        handlingBySeller.get(offer.seller_id) !== offer.handling_time
      ) {
        handlingBySeller.set(offer.seller_id, null);
      }

      cells[offer.seller_id] = { kind: "offer", offer };
    }

    // The Cheapest tag and the saving's ceiling are ACTIVE-scoped: a cheaper
    // unbuyable offer must not win the tag on a non-clickable cell, and a
    // dearer unbuyable one must not inflate the saving. `activePriceExtremes`
    // is the single owner of that rule; the old "only one supplier" guard is
    // folded into its >=2-active gate.
    const { cheapestSellerId, highestActivePrice } =
      activePriceExtremes(offers);

    // One row per line the buyer holds; a product with no line at all (not
    // reachable from `carts`, but cheap to keep total) still gets its row.
    for (const pick of picks.length > 0 ? picks : [undefined]) {
      let savingFormatted: string | undefined;
      if (pick && highestActivePrice !== null) {
        const currentOffer = offers.find((o) => o.seller_id === pick.sellerId);
        if (currentOffer) {
          const delta =
            (highestActivePrice -
              currentOffer.calculated_price.calculated_amount) *
            pick.quantity;
          if (delta > EPSILON) {
            savingFormatted = formatPrice(delta);
          }
        }
      }

      rows.push({
        rowKey: pick?.lineItemId ?? product.id,
        productId: product.id,
        productTitle: pick?.productTitle ?? product.id,
        variantTitle: pick?.variantTitle ?? null,
        quantity: pick?.quantity ?? 0,
        currentSellerId: pick?.sellerId ?? null,
        currentCartId: pick?.cartId ?? null,
        currentLineItemId: pick?.lineItemId ?? null,
        currentUnitPrice: pick?.unitPrice ?? null,
        // Each line gets its own copy: switching one line must not mutate the
        // cells another line of the same product is rendered from.
        cells: { ...cells },
        cheapestSellerId,
        savingFormatted
      });
    }
  }

  // Every product actually in the cart gets a row, even if the pricing
  // endpoint didn't return it (a failed/omitted batch in
  // `useProductComparisonPricing`, or a product it simply doesn't know
  // about) - otherwise that product just silently disappears from the
  // comparison instead of still being listed. There's no offer data to
  // compare against here, so it's priced from the line item itself, under
  // its own current supplier's column only.
  for (const [productId, picks] of Object.entries(currentPicks)) {
    if (pricedProductIds.has(productId)) continue;

    for (const pick of picks) {
      ensureColumn(pick.sellerId);

      rows.push({
        rowKey: pick.lineItemId,
        productId,
        productTitle: pick.productTitle,
        variantTitle: pick.variantTitle,
        quantity: pick.quantity,
        currentSellerId: pick.sellerId,
        currentCartId: pick.cartId,
        currentLineItemId: pick.lineItemId,
        currentUnitPrice: pick.unitPrice,
        cells: {
          [pick.sellerId]: {
            kind: "current-unpriced",
            unitPrice: pick.unitPrice
          }
        },
        cheapestSellerId: null,
        savingFormatted: undefined
      });
    }
  }

  const columns: ComparisonColumn[] = [...columnsMap.values()]
    .map((column) => ({
      ...column,
      handlingTime: handlingBySeller.get(column.sellerId) ?? null
    }))
    .sort((a, b) => a.sellerName.localeCompare(b.sellerName));

  // Fill in "not stocked" for every column a row didn't get an offer for.
  for (const row of rows) {
    for (const column of columns) {
      if (!row.cells[column.sellerId]) {
        row.cells[column.sellerId] = { kind: "not-stocked" };
      }
    }
  }

  // Sorted explicitly rather than left in `pricingProducts` fetch order: that
  // order isn't guaranteed to echo the (now-sorted) requested id order - a
  // product-list endpoint is free to return its own natural order - and
  // leaving it unsorted would let row position drift on every refetch
  // (e.g. after a supplier switch) even once the input ids are stable.
  // `productId` breaks ties so two identically-titled products don't swap, and
  // `rowKey` does the same for two lines of one product.
  rows.sort(
    (a, b) =>
      a.productTitle.localeCompare(b.productTitle) ||
      a.productId.localeCompare(b.productId) ||
      a.rowKey.localeCompare(b.rowKey)
  );

  return { rows, columns };
}

/**
 * What a switch changes, in the terms the `supplier_switched` event is reported
 * in. Derived here rather than at the call site because eligibility is already
 * computed from exactly this cell - so the surface that triggers a switch never
 * has to reach back into `row.cells[...].offer.calculated_price` to describe it,
 * and cannot describe it differently from how eligibility was decided.
 */
export interface SwitchChange {
  productId: string;
  variantId: string;
  fromSellerId: string;
  toSellerId: string;
  /** Unit price before the switch, in major units. */
  fromUnitPrice: number;
  /** Unit price after the switch, in major units. */
  toUnitPrice: number;
}

/** The exact payload `useSwitchSupplier` needs to move a product to a supplier. */
export interface SwitchTarget {
  offerId: string;
  quantity: number;
  /** The cart the product is currently in - the switch source to remove from. */
  sourceCartId: string;
  /** The line item to remove from `sourceCartId`. */
  lineItemId: string;
  /** What the switch changes, for reporting it once it lands. */
  change: SwitchChange;
}

/**
 * The single owner of switch-eligibility: whether the buyer can move `row`'s
 * product to `column`'s supplier, and if so the exact payload the switch needs.
 * Returns `null` when the pair isn't switchable - the cell isn't an ACTIVE
 * offer, it's already the current supplier, or the row has no current pick to
 * switch away from (no source cart / line item / unit price).
 *
 * Both sides read this, so the rule lives in ONE place: the grid table wires a
 * cell's click affordance only when this is non-null, and the switch handler
 * uses it as its guard AND its payload. Previously the "is it active / not the
 * current supplier" half lived in the cell and the "has a source cart/line"
 * half lived in the handler, and the two could disagree.
 *
 * `currentUnitPrice` is part of that guard rather than a caller's separate
 * check, so `change` is fully typed: the analytics path used to re-test it
 * downstream, which was the same split reopening on a different route.
 */
export function switchTargetFor(
  row: ComparisonProductRow,
  column: ComparisonColumn
): SwitchTarget | null {
  const cell = row.cells[column.sellerId];
  if (
    cell?.kind !== "offer" ||
    cell.offer.status !== SalonStockerHttpTypes.StoreSellerOfferStatus.ACTIVE ||
    row.currentSellerId === column.sellerId ||
    !row.currentSellerId ||
    !row.currentCartId ||
    !row.currentLineItemId ||
    row.currentUnitPrice === null
  ) {
    return null;
  }

  return {
    offerId: cell.offer.id,
    quantity: row.quantity,
    sourceCartId: row.currentCartId,
    lineItemId: row.currentLineItemId,
    change: {
      productId: row.productId,
      variantId: cell.offer.variant_id,
      fromSellerId: row.currentSellerId,
      toSellerId: column.sellerId,
      fromUnitPrice: row.currentUnitPrice,
      toUnitPrice: cell.offer.calculated_price.calculated_amount
    }
  };
}

/** The per-unit price a cell represents, or `null` for "not-stocked" (nothing to add). */
function cellUnitPrice(cell: ComparisonCell | undefined): number | null {
  if (cell?.kind === "offer")
    return cell.offer.calculated_price.calculated_amount;
  if (cell?.kind === "current-unpriced") return cell.unitPrice;
  return null;
}

/**
 * Per-supplier subtotal: the sum of only the products the customer is
 * CURRENTLY buying from that supplier (the purple-ringed cell in each row) -
 * not every product they merely carry. This mirrors what that supplier's
 * real seller-cart subtotal is for the products in this filtered view,
 * rather than a hypothetical "if I bought everything here" total. `null`
 * when the supplier isn't the current pick for any listed product (they're
 * shown purely for comparison, or every one of their picks was filtered out).
 */
export function computeSupplierSubtotals(
  rows: ComparisonProductRow[],
  columns: ComparisonColumn[]
): Record<string, number | null> {
  const subtotals: Record<string, number | null> = {};

  for (const column of columns) {
    let sum = 0;
    let isCurrentForAny = false;

    for (const row of rows) {
      if (row.currentSellerId !== column.sellerId) continue;
      const price = cellUnitPrice(row.cells[column.sellerId]);
      if (price !== null) {
        isCurrentForAny = true;
        sum += price * row.quantity;
      }
    }

    subtotals[column.sellerId] = isCurrentForAny ? sum : null;
  }

  return subtotals;
}
